import type { World } from '../types/world';
import { matchesLevelKey } from '../utils/ids';
import { getWorldId, loadWorld, loadWorldsManifest } from './worldLoader';

export interface CompletedLevelSummary {
  key: string;
  levelName?: string;
  worldName?: string;
  href?: string;
}

export function describeCompletedLevels(keys: string[], worlds: World[]): CompletedLevelSummary[] {
  return keys.map(key => {
    const matches = worlds.flatMap(world => world.levels
      .filter(level => matchesLevelKey(key, world.id, level.id))
      .map(level => ({ world, level })));
    // An old, unqualified ID can be ambiguous across worlds. Do not guess its name.
    if (matches.length !== 1) return { key };
    const { world, level } = matches[0];
    return {
      key,
      levelName: level.name,
      worldName: world.name,
      href: `/worlds/${encodeURIComponent(world.id)}/levels/${encodeURIComponent(level.id)}`,
    };
  });
}

export async function loadCompletedLevelSummaries(keys: string[]): Promise<CompletedLevelSummary[]> {
  if (keys.length === 0) return [];

  const manifest = await loadWorldsManifest();
  const hasLegacyKeys = keys.some(key => !key.includes(':'));
  const worldIds = new Set(keys.filter(key => key.includes(':')).map(key => key.slice(0, key.indexOf(':'))));
  // Include inactive worlds: progress can belong to another language or an older course.
  const entries = manifest.worlds.filter(entry => hasLegacyKeys || worldIds.has(getWorldId(entry)));
  const loaded = await Promise.all(entries.map(entry => loadWorld(getWorldId(entry), manifest)));
  if (loaded.some(world => world === null)) throw new Error('Unable to load completed exercise names.');
  return describeCompletedLevels(keys, loaded.filter((world): world is World => world !== null));
}
